import { Controller } from "@hotwired/stimulus"

/*
  Knowledge context-sets selector living inside the assistant "+" menu
  (issue #7641). Two modes, chosen by whether `urlValue` is present:

  - Persisted conversation (urlValue set): every toggle auto-saves the whole
    selection via PATCH to the conversation, mirroring the server's
    `context_selection` update path. Nothing is submitted with the message form.

  - New conversation (urlValue blank): the fields keep their `name` detached so
    an untouched panel submits nothing and the conversation inherits the
    org/global default. The checkboxes stay fully interactive; the first change
    reattaches the names (and flags the panel as touched via a data attribute) so
    the selection rides along with the first message — or, on the assistant home,
    so the omni-search controller serializes it into the hidden submission form.

  A third "plain" mode (urlValue blank, deferredValue false) is used by the
  organization defaults form: it submits normally via its own Save button, so the
  controller only wires the select-all / collapsible UI and leaves names attached.

  It also drives the "select all" parent for the autonomic-legislation subtree:
  toggling it mirrors to every child, and editing a child keeps the parent's
  checked / indeterminate state in sync.
*/
export default class extends Controller {
  static targets = ["field", "selectAll"]
  static values = { url: String, deferred: Boolean }

  connect() {
    // The "select all" parent is UI-only — it must never be submitted as a key.
    if (this.hasSelectAllTarget) this.selectAllTarget.removeAttribute("name")

    // Detaching the name (rather than disabling the input) keeps the checkbox
    // clickable while excluding it from an untouched submission.
    if (this.deferred) {
      this.fieldTargets.forEach((field) => {
        field.dataset.name = field.getAttribute("name")
        field.removeAttribute("name")
      })
    }
    this.syncSelectAll()
  }

  get persisted() {
    return this.hasUrlValue && this.urlValue !== ""
  }

  get deferred() {
    return this.deferredValue
  }

  // A child checkbox toggled directly.
  change() {
    this.syncSelectAll()
    this.commit()
  }

  // The autonomic "select all" parent toggled: mirror it to every child.
  toggleAll() {
    if (!this.hasSelectAllTarget) return

    const { checked } = this.selectAllTarget
    this.autonomicFields.forEach((field) => { field.checked = checked })
    this.selectAllTarget.indeterminate = false
    this.commit()
  }

  // Recompute the parent checkbox: checked when all children are, indeterminate
  // when only some are, unchecked when none.
  syncSelectAll() {
    if (!this.hasSelectAllTarget) return

    const children = this.autonomicFields
    const checkedCount = children.filter((field) => field.checked).length
    this.selectAllTarget.checked = checkedCount > 0 && checkedCount === children.length
    this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < children.length
  }

  // Child checkboxes governed by the "select all" parent (autonomic laws),
  // identified by living inside the parent's subtree container.
  get autonomicFields() {
    return this.fieldTargets.filter(
      (field) => field.type === "checkbox" && field.closest("[data-context-sources-autonomic]")
    )
  }

  // Reveal the selection to the surrounding form (new) or auto-save it (persisted).
  commit() {
    if (this.persisted) {
      this.save()
      return
    }

    // Plain mode (org defaults form): submitted via its own Save button.
    if (!this.deferred) return

    if (this.element.dataset.contextSourcesTouched !== "true") {
      this.element.dataset.contextSourcesTouched = "true"
      this.fieldTargets.forEach((field) => {
        if (field.dataset.name) field.setAttribute("name", field.dataset.name)
      })
    }
  }

  save() {
    const params = new URLSearchParams()
    params.append("context_selection", "1")
    // Empty entry so an all-unchecked selection persists as [] rather than
    // reverting to the org/global default.
    params.append("conversation[enabled_context_keys][]", "")
    this.checkedValues.forEach((value) => {
      params.append("conversation[enabled_context_keys][]", value)
    })

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/vnd.turbo-stream.html, application/json"
      },
      body: params.toString(),
      credentials: "same-origin"
    })
  }

  get checkedValues() {
    return this.fieldTargets
      .filter((field) => field.type === "checkbox" && field.checked)
      .map((field) => field.value)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
